import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Checkbox, Popover, Spin, Switch } from 'antd';
import {
	CloseOutlined,
	DownOutlined,
	FilterOutlined,
	HolderOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import ReactDragListView from 'react-drag-listview';
import { TechnicianAvatarWithNameEmail } from './TechnicianColumnHeader';
import OWSearchInput from '../common/OWSearchInput';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { cn } from '../../utils/ClassNamesUtils';
import { getContactName } from '../../utils/DataFormatterUtils';
import { getFieldTechContact, getFieldTechDisplayName } from '../../utils/FieldTechUtils';

const GROUP_MODE_ALL = 'all';
const GROUP_MODE_SUPERVISOR = 'supervisor';
const NO_SUPERVISOR_KEY = 'noSupervisor';
const NO_SUPERVISOR_LABEL = 'No supervisor';
// Row layout lives here, not in the shared avatar component: keep the avatar from shrinking and
// ellipsize the name/email lines that sit next to it.
const matchesSearch = (technician, searchText) => {
	if (!searchText) {
		return true;
	}
	const needle = searchText.toLowerCase();
	const name = getFieldTechDisplayName(technician).toLowerCase();
	const email = nullSafeGetOrElse('email', technician, '').toLowerCase();
	return name.indexOf(needle) >= 0 || email.indexOf(needle) >= 0;
};

const buildSupervisorGroups = (technicians) => {
	const groupsByKey = {};
	technicians.forEach((technician) => {
		const supervisors = nullSafeGetOrElse('inventorySupervisorContacts', technician, []).filter(
			(supervisor) => !!nullSafeGet('email', supervisor)
		);
		const entries =
			supervisors.length > 0
				? supervisors.map((supervisor) => ({
						key: nullSafeGetOrElse('email', supervisor, NO_SUPERVISOR_KEY),
						label: getContactName(supervisor).trim() || nullSafeGet('email', supervisor),
				  }))
				: [{ key: NO_SUPERVISOR_KEY, label: NO_SUPERVISOR_LABEL }];
		entries.forEach((entry) => {
			if (!groupsByKey[entry.key]) {
				groupsByKey[entry.key] = { key: entry.key, label: entry.label, technicians: [] };
			}
			groupsByKey[entry.key].technicians.push(technician);
		});
	});

	return Object.keys(groupsByKey)
		.map((groupKey) => groupsByKey[groupKey])
		.sort((firstGroup, secondGroup) => {
			if (firstGroup.key === NO_SUPERVISOR_KEY) {
				return 1;
			}
			if (secondGroup.key === NO_SUPERVISOR_KEY) {
				return -1;
			}
			return firstGroup.label.localeCompare(secondGroup.label);
		});
};

const moveArrayItem = (list: any[], fromIndex: number, toIndex: number): any[] => {
	const result = Array.from(list || []);
	if (fromIndex < 0 || fromIndex >= result.length || toIndex < 0 || toIndex >= result.length) {
		return result;
	}
	const [movedItem] = result.splice(fromIndex, 1);
	result.splice(toIndex, 0, movedItem);
	return result;
};

interface TechnicianFilterPopoverProps {
	technicians: any[];
	selectedEmails: string[];
	onChange: (orderedEmails: string[]) => void;
	/** Total the API reports, so the label doesn't count up as pages of techs arrive. */
	technicianCount?: number;
	/** No explicit filter yet — the label then reflects every tech, not just the loaded ones. */
	usingDefaultSelection?: boolean;
	fetching?: boolean;
	/** The full roster is still being paged in, so the control can't be filtered yet. */
	loading?: boolean;
}

export const TechnicianFilterPopover: FC<TechnicianFilterPopoverProps> = ({
	technicians,
	selectedEmails,
	onChange,
	technicianCount = 0,
	usingDefaultSelection = false,
	fetching,
	loading = false,
}) => {
	const [visible, setVisible] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [groupMode, setGroupMode] = useState(GROUP_MODE_ALL);

	const techniciansByEmail = useMemo(() => {
		const lookup = {};
		(technicians || []).forEach((technician) => {
			const email = nullSafeGet('email', technician);
			if (email) {
				lookup[email] = technician;
			}
		});
		return lookup;
	}, [technicians]);

	const [selectionSnapshot, setSelectionSnapshot] = useState<string[]>([]);

	const isSelected = useCallback((email) => selectedEmails.indexOf(email) >= 0, [selectedEmails]);

	const partitionSelectedFirst = useCallback(
		(list) => {
			const wasSelectedOnOpen = (technician) =>
				selectionSnapshot.indexOf(nullSafeGet('email', technician)) >= 0;
			return list
				.filter(wasSelectedOnOpen)
				.concat(list.filter((technician) => !wasSelectedOnOpen(technician)));
		},
		[selectionSnapshot]
	);

	const visibleTechnicians = useMemo(
		() => (technicians || []).filter((technician) => matchesSearch(technician, searchText)),
		[technicians, searchText]
	);

	const supervisorGroups = useMemo(
		() => buildSupervisorGroups(visibleTechnicians),
		[visibleTechnicians]
	);

	const allModeTechnicians = useMemo(
		() => partitionSelectedFirst(visibleTechnicians),
		[partitionSelectedFirst, visibleTechnicians]
	);

	const orderedTechnicians = useMemo(
		() => selectedEmails.map((email) => techniciansByEmail[email]).filter(Boolean),
		[selectedEmails, techniciansByEmail]
	);
	const hasColumnOrder = orderedTechnicians.length > 0;

	const toggleTechnician = useCallback(
		(email) => {
			if (!email) {
				return;
			}
			onChange(
				selectedEmails.indexOf(email) >= 0
					? selectedEmails.filter((selectedEmail) => selectedEmail !== email)
					: selectedEmails.concat(email)
			);
		},
		[selectedEmails, onChange]
	);

	const createToggleHandler = (email) => () => toggleTechnician(email);

	const createGroupSelectAllHandler = useCallback(
		(groupTechnicians) => (checked) => {
			const groupEmails = groupTechnicians
				.map((technician) => nullSafeGet('email', technician))
				.filter(Boolean);
			if (checked) {
				const additions = groupEmails.filter((email) => selectedEmails.indexOf(email) < 0);
				onChange(selectedEmails.concat(additions));
				return;
			}
			onChange(selectedEmails.filter((email) => groupEmails.indexOf(email) < 0));
		},
		[selectedEmails, onChange]
	);

	const allModeSelectedCount = useMemo(
		() =>
			allModeTechnicians.filter((technician) => isSelected(nullSafeGet('email', technician)))
				.length,
		[allModeTechnicians, isSelected]
	);

	const handleClearAll = useCallback(() => onChange([]), [onChange]);
	const handleClose = useCallback(() => setVisible(false), []);
	const handleSearchChange = useCallback((value) => setSearchText(value || ''), []);
	const showAllTechnicians = useCallback(() => setGroupMode(GROUP_MODE_ALL), []);
	const showBySupervisor = useCallback(() => setGroupMode(GROUP_MODE_SUPERVISOR), []);

	const handleVisibleChange = useCallback(
		(nextVisible) => {
			if (nextVisible) {
				setSelectionSnapshot(selectedEmails);
			}
			setVisible(nextVisible);
		},
		[selectedEmails]
	);

	const dragProps = useMemo(
		() => ({
			nodeSelector: '.technicianOrderRow',
			handleSelector: '.technicianOrderHandle',
			onDragEnd(fromIndex, toIndex) {
				onChange(moveArrayItem(selectedEmails, fromIndex, toIndex));
			},
		}),
		[selectedEmails, onChange]
	);

	const renderTechnicianRow = (technician) => {
		const email = nullSafeGet('email', technician);
		return (
			<div
				key={email}
				className="flex cursor-pointer flex-row items-center gap-2 rounded-sm px-1 hover:bg-grey-3"
				onClick={createToggleHandler(email)}
			>
				<Checkbox className="flex-none" checked={isSelected(email)} />
				<TechnicianAvatarWithNameEmail technician={technician} />
			</div>
		);
	};

	const renderGroup = (group) => {
		const groupEmails = group.technicians
			.map((technician) => nullSafeGet('email', technician))
			.filter(Boolean);
		const allSelected =
			groupEmails.length > 0 && groupEmails.every((email) => selectedEmails.indexOf(email) >= 0);
		return (
			<div className="flex flex-col gap-2" key={group.key}>
				<div className="flex flex-row items-center justify-between border-0 border-b border-solid border-grey-5 py-1">
					<span className="footnoteregular uppercase text-character-secondary">{group.label}</span>
					<div className="flex flex-row items-center gap-2">
						<span className="footnoteregular text-character-secondary">Select all</span>
						<Switch
							size="small"
							checked={allSelected}
							onChange={createGroupSelectAllHandler(group.technicians)}
						/>
					</div>
				</div>
				{partitionSelectedFirst(group.technicians).map(renderTechnicianRow)}
			</div>
		);
	};

	const content = (
		<div className="technicianFilterPopover">
			<div className="flex flex-row items-center justify-between gap-3 border-0 border-b border-solid border-grey-5 p-4">
				<span className="h5medium text-character-primary">Technicians</span>
				<div className="flex flex-row items-center gap-2">
					<Button type="link" size="small" onClick={handleClearAll}>
						Clear all
					</Button>
					<Button type="text" size="small" icon={<CloseOutlined />} onClick={handleClose} />
				</div>
			</div>
			<div className="flex flex-row">
				<div className="flex w-[320px] flex-col gap-3 p-4">
					<OWSearchInput
						placeholder="Search technicians"
						onChange={handleSearchChange}
						style={{ width: '100%' }}
					/>
					<div className="flex flex-row items-center gap-2">
						<Button
							shape="round"
							size="small"
							type={groupMode === GROUP_MODE_ALL ? 'primary' : 'default'}
							onClick={showAllTechnicians}
						>
							All
						</Button>
						<Button
							shape="round"
							size="small"
							type={groupMode === GROUP_MODE_SUPERVISOR ? 'primary' : 'default'}
							onClick={showBySupervisor}
						>
							By Supervisor
						</Button>
					</div>
					<div className="max-h-[300px] overflow-y-auto">
						{fetching ? (
							<div className="flex flex-row justify-center py-6">
								<Spin />
							</div>
						) : visibleTechnicians.length === 0 ? (
							<div className="bodyregular py-6 text-center text-character-secondary">
								No technicians found
							</div>
						) : groupMode === GROUP_MODE_ALL ? (
							<div className="flex flex-col gap-3">
								<div
									className="footnoteregular flex cursor-pointer flex-row items-center gap-2 border-0 border-b border-solid border-grey-5 py-1 uppercase text-character-secondary"
									onClick={() =>
										createGroupSelectAllHandler(allModeTechnicians)(
											allModeSelectedCount < allModeTechnicians.length
										)
									}
								>
									<Checkbox
										className="flex-none"
										checked={allModeSelectedCount === allModeTechnicians.length}
										indeterminate={
											allModeSelectedCount > 0 && allModeSelectedCount < allModeTechnicians.length
										}
									/>
									<span>All technicians</span>
								</div>
								{allModeTechnicians.map(renderTechnicianRow)}
							</div>
						) : (
							<div className="flex flex-col gap-3">{supervisorGroups.map(renderGroup)}</div>
						)}
					</div>
				</div>
				{/* Stays mounted so it can slide in and out; the outer track collapses its width while the
				    inner panel keeps its own, so the content never reflows mid-animation. */}
				<div
					className={cn(
						'overflow-hidden transition-[width] duration-300 ease-out',
						hasColumnOrder ? 'w-[320px]' : 'w-0'
					)}
				>
					<div
						className={cn(
							'flex w-[320px] flex-col gap-2 border-0 border-l border-solid border-grey-4 bg-grey-2 p-4 transition-transform duration-300 ease-out h-full',
							hasColumnOrder ? 'translate-x-0' : 'translate-x-full'
						)}
					>
						<span className="footnoteregular border-0 border-b border-solid border-grey-5 py-1 uppercase text-character-secondary">
							Column order
						</span>
						<div className="max-h-[360px] overflow-y-auto">
							<ReactDragListView {...dragProps}>
								<div className="flex flex-col gap-2">
									{orderedTechnicians.map((technician) => {
										const email = nullSafeGet('email', technician);
										return (
											<div
												key={email}
												className="technicianOrderRow flex flex-row items-center gap-2"
											>
												<HolderOutlined className="technicianOrderHandle flex-none cursor-move text-grey-7" />
												<TechnicianAvatarWithNameEmail technician={technician} />
												<Button
													type="text"
													size="small"
													className="flex-none"
													icon={<CloseOutlined />}
													onClick={createToggleHandler(email)}
												/>
											</div>
										);
									})}
								</div>
							</ReactDragListView>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	return (
		<Popover
			trigger="click"
			placement="bottomLeft"
			visible={visible}
			onVisibleChange={handleVisibleChange}
			content={content}
			overlayClassName="technicianFilterPopover__overlay"
		>
			<Button className="flex flex-row items-center gap-1" disabled={loading}>
				{loading ? <LoadingOutlined /> : <FilterOutlined />}
				<span>Techs ({usingDefaultSelection ? technicianCount : selectedEmails.length})</span>
				<DownOutlined className="text-xs" />
			</Button>
		</Popover>
	);
};
